import React, { useState } from "react";
import { ReactSVG } from "react-svg";
import { Check } from "lucide-react";
import check from "../../Asset/check.svg";
import fillCheck from "../../Asset/fill check.svg";
import { useSelector } from "react-redux";
import { selectUser } from "../../Redux/authSlice";
import { NavLink, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ButtonLoading from "../../component/ButtonLoading";
import { trackEvent } from "../../lib/mixpanel";
import {
  TEAMS_PLAN_VISIBLE,
  TEAM_ANNUAL_SAVING_PCT,
  TEAM_MIN_SEATS,
  TEAM_PRICE_PER_SEAT,
  formatUsd,
  teamSeatPeriodPrice,
} from "../Team/teamPricing";

// Everything a seat includes. Kept here rather than in teamPricing.js because
// this is marketing copy for the pricing card, not billing logic.
const TEAM_FEATURES = [
  "ZT Pro for every member on Android, iOS, desktop and web",
  "One bill with invoices, tax and your VAT number",
  "Invite by email, add admins, remove anyone",
  "Admins never see transcripts, notes or prompts",
  `From ${TEAM_MIN_SEATS} seats. Employees never pay.`,
];

const PricingSection = () => {
  const user = useSelector(selectUser);
  const [isAnnual, setIsAnnual] = useState(true);
  const [isTeamAnnual, setIsTeamAnnual] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubscription = async () => {
    if (!user) {
      navigate("/login");
      return;
    }

    setIsLoading(true);

    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}api/stripe/create-subscription-session`;

      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: user?.EmailId,
          name: `${user?.FirstName ?? ""} ${user?.LastName ?? ""}`.trim(),
          // ✅ UPDATED (new plan codes)
          plan: isAnnual ? "Pro_Annual" : "Pro",
        }),
      });

      let payload = null;
      try {
        payload = await res.json();
      } catch {
        // ignore non-json
      }

      if (!res.ok) {
        const msg =
          payload?.message ||
          payload?.error ||
          res.statusText ||
          "Failed to create subscription session.";
        toast.error(msg);
        return;
      }

      if (payload?.success && payload?.checkoutToken) {
        trackEvent("subscription_started", {
          plan_type: isAnnual ? "Pro_Annual" : "Pro",
          billing_cycle: isAnnual ? "annual" : "monthly",
        });
        // The URL carries only the short-lived token, never the client secret.
        navigate(`/checkout/${payload.checkoutToken}`);
        return;
      }

      toast.error(
        payload?.message || payload?.error || "Unexpected server response."
      );
    } catch (err) {
      toast.error(err?.message || "Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Teams isn't bought from here: seats and billing cycle are chosen on the
  // Teams tab of Manage Subscription. /settings is protected, so a logged-out
  // visitor is sent to login and returned to this exact URL afterwards.
  const handleTeamSetup = () => {
    const interval = isTeamAnnual ? "annual" : "monthly";
    trackEvent("team_plan_clicked", {
      source: "pricing_card",
      billing_cycle: interval,
      price_per_seat: TEAM_PRICE_PER_SEAT[interval],
    });
    navigate(`/settings?plan=teams&billing=${interval}`);
  };

  return (
    <div className="w-full  flex justify-center items-center md:px-4 p-0">
      <div className="flex flex-col lg:flex-row lg:gap-0 items-center gap-6 max-w-6xl w-full">
        {/* Free Plan */}
        <div className="flex-1  md:rounded-s-3xl rounded-3xl bg-[#F6FBFE] lg:w-full md:w-[80%] w-full flex flex-col items-start md:gap-4 gap-3  p-10 lg:pr-16 lg:-mr-8">
          <h1 className="bg-gradient-to-r leading-none from-[#3AB1F6] to-[#407BFF] bg-clip-text text-transparent w-max font-semibold font-inter md:text-[1.375rem] text-base">
            ZT Basic
          </h1>
          <h2 className="md:text-[3.445rem] text-[2.5rem] leading-none font-semibold font-inter">
            Free
          </h2>
          <p className="md:text-[1.375rem] text-base leading-none font-medium font-inter">
            Start your ZT journey:
          </p>
          <ul className="space-y-4 ">
            {[
              "15 minutes audio transcription per week",
              "Personal Dictionary",
              "Basic Prompt Selection",
              "Translate and Transcribe audio",
            ].map((feature) => (
              <li
                key={feature}
                className="flex items-center md:text-[1.076rem] text-sx  font-inter text-[#000000] gap-2"
              >
                <ReactSVG
                  src={check}
                  beforeInjection={(svg) => {
                    svg.setAttribute(
                      "class",
                      "md:w-[1.813rem] w-5 md:h-[1.813rem] h-5"
                    );
                  }}
                />
                {feature}
              </li>
            ))}
          </ul>

          <NavLink
            to={"/download"}
            className="md:mt-8 mt-6 w-full rounded-full border bg-transparent border-[#3AB1F6]  hover:bg-gradient-to-r hover:from-[#3AB1F6] hover:to-[#407BFF]  transition"
          >
            <span className="block w-full rounded-full bg-white py-3 text-center font-inter md:text-[1.076rem] text-sx font-semibold text-transparent bg-clip-text bg-gradient-to-r from-[#3AB1F6] to-[#407BFF] transition-all duration-300 hover:bg-none hover:text-white hover:bg-gradient-to-r hover:from-[#3AB1F6] hover:to-[#407BFF]">
              Get Started Today
            </span>
          </NavLink>
        </div>

        {/* Pro Plan */}
        <div className="flex-1 rounded-3xl bg-[#EBF7FE] border border-[#407BFF] text-black lg:w-full md:w-[80%] w-full flex flex-col items-start md:gap-4 gap-3 md:shadow-2xl shadow-xl p-10 relative overflow-hidden">
          {/* Toggle */}
          <div className="flex justify-center w-full">
            <div className="bg-white p-2 rounded-full flex gap-1 w-full md:text-[1.22rem] text-sm  font-semibold font-inter">
              <button
                onClick={() => setIsAnnual(false)}
                className={`flex-1  py-3 rounded-full text-sm font-medium transition ${
                  !isAnnual
                    ? " bg-gradient-to-r from-[#3AB1F6] to-[#407BFF] hover:from-[#3AA6E4] hover:to-[#4175EB] transition-all duration-300 ease-in-out text-white"
                    : "text-gray-700"
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setIsAnnual(true)}
                className={`flex-1  py-3 rounded-full text-sm font-medium transition ${
                  isAnnual
                    ? "bg-gradient-to-r from-[#3AB1F6] to-[#407BFF] hover:from-[#3AA6E4] hover:to-[#4175EB] transition-all duration-300 ease-in-out text-white"
                    : "text-gray-700"
                }`}
              >
                Annual{" "}
                <span className={`ml-1.5 inline-flex items-center text-[0.625rem] font-bold px-1.5 py-0.5 rounded-full leading-none ${isAnnual ? "bg-white text-[#407BFF]" : "bg-[#EBF7FE] text-[#407BFF]"}`}>
                  20% off
                </span>
              </button>
            </div>
          </div>

          <h1 className="bg-gradient-to-r leading-none from-[#3AB1F6] to-[#407BFF] bg-clip-text text-transparent w-max font-semibold font-inter md:text-[1.375rem] text-base">
            ZT Pro
          </h1>
          <h2 className="md:text-[3.445rem] text-[2.5rem] leading-none font-semibold font-inter">
            ${isAnnual ? "8.00" : "9.99"}{" "}
            <span className="md:text-[1.365rem] text-base font-inter">
              /month
            </span>
          </h2>
          <p className="md:text-[1.375rem] text-base  font-medium font-inter">
            Get all premium ZT features. Everything in basic plus:
          </p>
          <ul className="space-y-4 ">
            {[
              "Unlimited words per week",
              "Unlimited audio transcription per week",
              "Custom prompt creation",
              "Early access to new features",
            ].map((feature) => (
              <li
                key={feature}
                className="flex items-center md:text-[1.076rem] text-sx font-inter text-[#000000] gap-2"
              >
                <ReactSVG
                  src={fillCheck}
                  beforeInjection={(svg) => {
                    svg.setAttribute(
                      "class",
                      "md:w-[1.813rem] w-5 md:h-[1.813rem] h-5"
                    );
                  }}
                />
                {feature}
              </li>
            ))}
          </ul>

          <button
            onClick={handleSubscription}
            disabled={isLoading}
            className={`mt-8 ${
              isLoading ? "opacity-50 cursor-not-allowed" : ""
            } w-full rounded-full border bg-transparent hover:border-[#3AB1F6] border-transparent bg-gradient-to-r from-[#3AB1F6] to-[#407BFF] hover:from-[#3AA6E4] hover:to-[#4175EB] transition-all duration-300 ease-in-out `}
          >
            <span className=" w-full flex items-center justify-center rounded-full  py-3 text-center font-inter md:text-[1.068rem] text-sx font-semibold text-white ">
              {isLoading ? (
                <ButtonLoading text="Subscribing..." />
              ) : (
                "Get Started Today"
              )}
            </span>
          </button>
        </div>

        {/* Teams Plan — dark card, sits apart from the Basic/Pro pair */}
        {TEAMS_PLAN_VISIBLE && (
        <div className="flex-1 rounded-3xl bg-[#0B1220] text-white lg:w-full md:w-[80%] w-full flex flex-col md:shadow-2xl shadow-xl p-10 relative overflow-hidden lg:ml-6">
          {/* Switch at the top, like ZT Pro: annual first and selected by default */}
          <div className="flex w-full rounded-full bg-white/10 p-1.5 gap-1 font-inter text-sm font-medium">
            {[
              { annual: false, label: "Monthly" },
              { annual: true, label: "Annual" },
            ].map(({ annual, label }) => {
              const active = isTeamAnnual === annual;
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => setIsTeamAnnual(annual)}
                  aria-pressed={active}
                  className={`flex flex-1 items-center justify-center rounded-full py-2.5 transition-all duration-300 ${
                    active
                      ? "bg-gradient-to-r from-[#3AB1F6] to-[#407BFF] text-white shadow"
                      : "text-white/60 hover:text-white"
                  }`}
                >
                  {label}
                  {annual && (
                    <span
                      className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[0.625rem] font-bold leading-none ${
                        active ? "bg-white text-[#407BFF]" : "bg-white/15 text-white/80"
                      }`}
                    >
                      {TEAM_ANNUAL_SAVING_PCT}% off
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="mt-5 flex w-full items-center justify-between gap-3">
            <h1 className="bg-gradient-to-r leading-none from-[#3AB1F6] to-[#407BFF] bg-clip-text text-transparent w-max font-bold font-inter md:text-[1.6rem] text-xl">
              ZT Teams
            </h1>
            <span className="rounded-full bg-white px-2.5 py-1 font-inter text-[0.625rem] font-bold uppercase tracking-[0.08em] text-[#0B1220]">
              New
            </span>
          </div>

          <p className="mt-2 font-inter text-sm leading-snug text-white/60">
            Pro for everyone at your organization, on one bill:
          </p>

          <h2 className="mt-4 flex items-baseline gap-2 font-inter">
            <span className="md:text-[2.75rem] text-[2.25rem] leading-none font-bold">
              ${formatUsd(TEAM_PRICE_PER_SEAT[isTeamAnnual ? "annual" : "monthly"], { trimZeros: true })}
            </span>
            <span className="text-sm font-normal text-white/70">per seat /month</span>
          </h2>
          <p className="mt-1.5 font-inter text-xs text-white/45">
            {isTeamAnnual
              ? `Billed yearly, $${formatUsd(teamSeatPeriodPrice("annual"))} per seat`
              : "Billed monthly"}
          </p>

          <div className="my-5 h-px w-full bg-white/10" />

          <ul className="space-y-3">
            {TEAM_FEATURES.map((feature) => (
              <li key={feature} className="flex items-start gap-2.5 font-inter text-sm text-white/85">
                <span className="mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-[#407BFF]/20">
                  <Check className="h-3 w-3 text-[#3AB1F6]" strokeWidth={3} />
                </span>
                {feature}
              </li>
            ))}
          </ul>

          <button
            onClick={handleTeamSetup}
            className="mt-6 w-full rounded-full bg-gradient-to-r from-[#3AB1F6] to-[#407BFF] py-3 text-center font-inter text-sm font-semibold text-white transition-all duration-300 hover:from-[#3AA6E4] hover:to-[#4175EB]"
          >
            Set Up Your Team
          </button>
        </div>
        )}
      </div>
    </div>
  );
};

export default PricingSection;
